<template>
  <div>
    <b-tabs v-model="selectedTab"
            card>

      <b-row align-h="between"
             class="container-fluid"
             no-gutters>

        <b-col cols="auto">
          <b-row id="filters">
            <b-col cols="auto">
              <b-button :pressed.sync="showFilter"
                        class="mb-2 mr-2">
                FILTER
              </b-button>
            </b-col>
          </b-row>
        </b-col>

        <b-col cols="auto">
          <b-row class="mt-3">
            <b-col v-if="isProposalsToZipEnabled"
                   class="mb-2"
                   cols="auto">
              <button-spinner :loading="zipLoading"
                              :spinner="{color: 'warning'}"
                              variant="info"
                              @click="downloadZipWrapper">
                <template v-slot:defaultText>
                  <span>PROPOSALS TO ZIP ({{ buttonZipTextAppend }})</span>
                </template>
              </button-spinner>
            </b-col>
          </b-row>
        </b-col>

      </b-row>

      <b-tab :title="mainTabTitle"
             active>
        <rapid-access-grade-table :gradeHelpers="gradeHelpers"
                                  :isManagementEvaluation="isManagementEvaluation"
                                  :loading="loading"
                                  :parent="id"
                                  :proposals="proposals"
                                  :userElevatedPermissions="userElevatedPermissions"
                                  @filtered-proposals="updateFilteredProposals($event, 0)"
                                  @update-proposal="updateProposal($event)"/>
      </b-tab>
      <template v-if="!isManagementEvaluation">
        <b-tab title="Reviews">
          <rapid-access-final-grade-reviews v-if="helpersDataLoaded"
                                            :parent="id"
                                            :proposals="proposals"
                                            @update-proposal="updateProposal($event)"
                                            @filtered-proposals="updateFilteredProposals($event, 1)"/>
        </b-tab>
      </template>
      <b-tab lazy
             title="Reports">
        <experiment-list v-if="helpersDataLoaded"
                         :elevated="false"
                         :load_data_url="finalGradeHelpers.experimentsUrl"
                         :prop_fields="finalGradeHelpers.finalGradeReportFields"
                         :show_filter="showFilter"
                         :show_zip_button="true"/>
      </b-tab>
      <template v-if="!isManagementEvaluation">
        <b-tab v-if="userElevatedPermissions.isEditor"
               title="Add Reviewer">
          <rapid-access-final-grade-select-reviewers :parent-id="id"
                                                     :proposals="proposals"
                                                     @setup-proposal="emitSetupProposal($event)"/>
        </b-tab>
      </template>
    </b-tabs>
  </div>
</template>

<script>
  import ButtonSpinner from '@/components/buttonSpinner';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import ExperimentList from '@/components/proposal/experiment-report/experiment_list';
  import RapidAccessFinalGradeReviews
    from '@/components/evaluation/rapid-access/final-grade/RapidAccessFinalGradeReviews';
  import RapidAccessFinalGradeSelectReviewers
    from '@/components/evaluation/rapid-access/final-grade/RapidAccessFinalGradeSelectReviewers';
  import RapidAccessGradeTable from '@/components/evaluation/RapidAccessGradeTable';

  export default {
    name: 'RapidAccessGradeDashboard',
    components: {
      ButtonSpinner,
      ExperimentList,
      RapidAccessFinalGradeSelectReviewers,
      RapidAccessFinalGradeReviews,
      RapidAccessGradeTable,
    },
    mixins: [infrastructureMixin],
    props: {
      gradeHelpers: {
        type: Object,
        required: true,
      },
      isManagementEvaluation: {
        type: Boolean,
        default: false,
      },
      mainTabTitle: {
        type: String,
        default: 'Rapid access scientific merit final grades',
      },
    },
    data() {
      return {
        proposals: [],
        filteredProposals: {},
        allProposalsLengths: {},
        buttonZipTextAppend: 'ALL',
        id: 0,
        roles: [],
        showFilter: true,
        selectedTab: 0,
        loading: false,
        zipLoading: false,
        helpersDataLoaded: false,
        finalGradeHelpers: {},
      };
    },
    computed: {
      isProposalsToZipEnabled() {
        return (this.selectedTab < 2) && (this.selectedTab !== this.reportsTabId);
      },
      reportsTabId() {
        return this.isManagementEvaluation ? 1 : 2;
      },
      userElevatedPermissions() {
        const permissions = this.$store.getters['login/getLoggedUserAttribute']('permissions') || [];
        return {
          isEditor: permissions.includes(this.gradeHelpers.editorPermission),
          isSubmitter: permissions.includes(this.gradeHelpers.submitterPermission),
        };
      },
    },
    watch: {
      selectedTab() {
        this.$emit('refresh');
        this.updateButtonZipTextAppend();
      },
      showFilter() {
        this.$emit('toggle-show');
      },
    },
    async created() {
      const finalGradeHelpers = await this.getEvaluationDashboardProperties(this.infrastructure, 'FinalGrade');
      this.finalGradeHelpers = finalGradeHelpers.getAsObject(
        'experimentsUrl',
        'finalGradeReportFields',
      );
      this.helpersDataLoaded = true;

      this.roles = this.$store.getters['login/getLoggedUserAttribute']('roles') || [];
      await this.loadData();
    },
    methods: {
      updateFilteredProposals(proposals, tabId) {
        this.filteredProposals[tabId] = proposals;
        this.updateButtonZipTextAppend();
      },
      updateButtonZipTextAppend() {
        if (this.filteredProposals[this.selectedTab] === undefined
          || this.allProposalsLengths[this.selectedTab] === undefined) {
          this.buttonZipTextAppend = 'ALL';
        } else {
          const allProposalsLength = this.allProposalsLengths[this.selectedTab];
          const filteredProposalsLength = this.filteredProposals[this.selectedTab].length;
          this.buttonZipTextAppend = allProposalsLength === filteredProposalsLength ? 'ALL'
            : filteredProposalsLength.toString();
        }
      },
      filteredProposalIDRange() {
        const uniqueProposalIds = new Set();
        this.filteredProposals[this.selectedTab].forEach((obj) => {
          if (obj.document_specification && obj.document_specification.document_id) {
            uniqueProposalIds.add(obj.document_specification.document_id);
          }
        });
        return Array.from(uniqueProposalIds);
      },
      async downloadZipWrapper() {
        this.zipLoading = true;
        // eslint-disable-next-line camelcase
        const proposalIDRange = { proposal_id_range: this.filteredProposalIDRange() };
        const callParam = { call: null };
        await this.downloadZip(callParam, proposalIDRange);
        this.zipLoading = false;
      },
      updateLocalProposalsData(data) {
        /* eslint-disable camelcase */
        this.proposals = data.map((proposal) => {
          if (this.isManagementEvaluation) {
            const { management_evaluation = {} } = proposal;
            management_evaluation.acceptance_mark = management_evaluation.acceptance_mark || '';
            return { ...proposal, management_evaluation };
          }
          const { final_grade = {} } = proposal;
          final_grade.acceptance_mark = final_grade.acceptance_mark || '';
          return { ...proposal, final_grade };
          /* eslint-enable camelcase */
        });
      },
      emitAndSyncProposalsData() {
        // setup the data in proposal-filter component
        this.$emit('setup-proposal', this.proposals);
        // load data from children component
        this.$emit('load-data');
        this.allProposalsLengths = { 0: this.proposals.length };
        if (!this.isManagementEvaluation) {
          this.allProposalsLengths[1] = this.proposals.length;
        }
      },
      async loadData() {
        this.loading = true;
        const gradeTypePath = this.isManagementEvaluation ? 'management-evaluation' : 'final-grades';
        const response = await this.axios.get(`/documents/rapid-access/${gradeTypePath}/${this.infrastructure}`);
        if (response.data) {
          this.updateLocalProposalsData(response.data.documents);
          this.emitAndSyncProposalsData();
          this.loading = false;
        } else {
          this.$notify({ type: 'error', title: 'Something went wrong' });
          this.proposals = [];
          this.loading = false;
        }
      },
      updateProposal(proposal) {
        const index = this.proposals.findIndex((el) => this.getId(el) === this.getId(proposal));
        if (index >= 0) {
          this.$set(this.proposals, index, proposal);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .card {
    margin: 4rem;
  }

  #filters {
    margin-top: 1rem;
  }
</style>
